import { useState, type FormEvent } from 'react';
import { ajouterFaitSchema, modifierFaitSchema } from '@colette/shared';
import type { Fait, SujetDeFait } from '../../api/types';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { Champ } from './Champ';
import { Alerte } from './CadreAuth';
import { BoutonDeuxGestes } from './BoutonDeuxGestes';

/**
 * Ce que Colette sait de quelqu'un — ou du foyer entier (etape B1, 2026-09-09).
 *
 * Pourquoi cet ecran existe : de chaque personne, Colette ne connaissait qu'un prenom et
 * une date de naissance. Il fallait donc lui redire a chaque demande l'ecole des enfants,
 * la gare, l'allergie — c'est-a-dire porter soi-meme la charge qu'elle est censee prendre.
 *
 * Trois regles de forme, chacune payee ailleurs dans ce depot :
 *
 * - Une LIGNE libre, jamais un formulaire a cases. Les cases qu'on imaginerait seraient
 *   francaises, donc fausses pour un foyer senegalais — meme erreur que le fuseau code
 *   en dur.
 * - Le contenu d'abord, les actions sur LEUR PROPRE RANGEE. Sur un telephone de 390 px
 *   une carte n'offre que ~280 px : un titre plus deux boutons sur la meme ligne laisse
 *   au texte une largeur negative (mesure du 2026-08-29, l'erreur a ete faite deux fois).
 * - Oublier demande DEUX gestes. C'est destructeur, et un pouce qui glisse ne doit pas
 *   suffire.
 */
export function FaitsDe({
  sujet,
  aPropos,
  titre,
  faits,
  onChange,
  signaler,
}: {
  sujet: SujetDeFait;
  /** Comment on nomme le sujet dans les libelles : « Lila », « le foyer ». */
  aPropos: string;
  /**
   * Un titre au-dessus de la liste, quand elle ne suit pas une fiche qui la nomme deja.
   *
   * Vu a l'ecran le 2026-09-09 : les faits du FOYER se collaient sous la derniere
   * personne, sans rien qui les en separe — qui lit de haut en bas les lui attribuait.
   */
  titre?: string;
  faits: Fait[];
  onChange: () => void;
  signaler: (message: string) => void;
}) {
  // `null` : rien d'ouvert. `''` : le formulaire d'ajout. Un identifiant : la correction
  // de ce fait-la. Un seul etat, donc jamais deux formulaires ouverts a la fois.
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const libelleChamp = `Ce que Colette doit retenir sur ${aPropos}`;

  const ouvrir = (id: string, valeurInitiale: string) => {
    // On repart TOUJOURS de l'etat actuel : une saisie abandonnee hier ne doit pas
    // reapparaitre a moitie ecrite aujourd'hui.
    setTexte(valeurInitiale);
    setErreur(null);
    setEnvoi(false);
    setOuvert(id);
  };

  const enregistrer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi || ouvert === null) return;
    setErreur(null);

    // Le MEME schema que l'API : le message affiche a la saisie ne peut pas diverger de
    // la regle appliquee par le serveur.
    const verifie =
      ouvert === ''
        ? ajouterFaitSchema.safeParse({ sujet, texte })
        : modifierFaitSchema.safeParse({ texte });
    if (!verifie.success) {
      setErreur(verifie.error.issues[0]?.message ?? 'Vérifie ce que tu as écrit');
      return;
    }

    setEnvoi(true);
    try {
      if (ouvert === '') await api.retenirUnFait(verifie.data as { sujet: SujetDeFait; texte: string });
      else await api.corrigerUnFait(ouvert, (verifie.data as { texte: string }).texte);
      setOuvert(null);
      onChange();
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  const oublier = async (id: string) => {
    try {
      await api.oublierUnFait(id);
      onChange();
    } catch (cause) {
      signaler(messageDErreur(cause));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 4 }}>
      {titre === undefined ? null : (
        <div style={{ fontSize: 12, fontWeight: 800, color: C.secondaire, marginTop: 6 }}>
          {titre}
        </div>
      )}
      {faits.map((fait) =>
        ouvert === fait.id ? (
          <Formulaire
            key={fait.id}
            libelle={libelleChamp}
            texte={texte}
            onTexte={setTexte}
            erreur={erreur}
            envoi={envoi}
            onAnnuler={() => setOuvert(null)}
            onEnregistrer={enregistrer}
          />
        ) : (
          <div
            key={fait.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: '9px 12px',
              borderRadius: 12,
              background: C.carteDouce,
              border: `1px solid ${C.bordure}`,
            }}
          >
            <div style={{ fontSize: 13, color: C.encre, lineHeight: 1.45 }}>{fait.texte}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                aria-label={`Corriger : ${fait.texte}`}
                onClick={() => ouvrir(fait.id, fait.texte)}
                style={styleActionDiscrete}
              >
                Corriger
              </button>
              <BoutonDeuxGestes
                libelle="Oublier"
                libelleArme="Confirmer"
                aria={`Oublier : ${fait.texte}`}
                ariaArme={`Confirmer l’oubli : ${fait.texte}`}
                onConfirmer={() => void oublier(fait.id)}
                style={{ ...styleActionDiscrete, color: C.corailTexte }}
                styleArme={{ background: C.corailFond, borderColor: C.corailTexte }}
              />
            </div>
          </div>
        ),
      )}

      {ouvert === '' ? (
        <Formulaire
          libelle={libelleChamp}
          texte={texte}
          onTexte={setTexte}
          erreur={erreur}
          envoi={envoi}
          onAnnuler={() => setOuvert(null)}
          onEnregistrer={enregistrer}
        />
      ) : (
        <button
          type="button"
          aria-label={`Ajouter ce que Colette doit retenir sur ${aPropos}`}
          onClick={() => ouvrir('', '')}
          style={{ ...styleActionDiscrete, alignSelf: 'flex-start', color: C.violetTexte }}
        >
          + Ce que Colette doit retenir
        </button>
      )}
    </div>
  );
}

const styleActionDiscrete = {
  border: `1px solid ${C.bordure}`,
  background: 'transparent',
  color: C.secondaire,
  borderRadius: 10,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 800,
  fontFamily: 'inherit',
  cursor: 'pointer',
} as const;

function Formulaire({
  libelle,
  texte,
  onTexte,
  erreur,
  envoi,
  onAnnuler,
  onEnregistrer,
}: {
  libelle: string;
  texte: string;
  onTexte: (v: string) => void;
  erreur: string | null;
  envoi: boolean;
  onAnnuler: () => void;
  onEnregistrer: (evenement: FormEvent) => void;
}) {
  return (
    <form
      onSubmit={onEnregistrer}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 12,
        borderRadius: 12,
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        // Sans cela, un element flex refuse de descendre sous la taille de son contenu
        // et DEBORDE au lieu de se tasser (piege du 2026-08-28).
        minWidth: 0,
      }}
    >
      <Champ
        libelle={libelle}
        valeur={texte}
        onChange={onTexte}
        aide="Une ligne suffit : « sort de l’école à 16h30 », « allergique aux arachides »."
        autoFocus
      />
      {erreur !== null ? <Alerte message={erreur} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onAnnuler}
          style={{
            flex: 1,
            height: 42,
            borderRadius: 12,
            border: `1px solid ${C.bordure}`,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={envoi}
          style={{
            flex: 2,
            height: 42,
            borderRadius: 12,
            border: 'none',
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {envoi ? 'Enregistrement…' : 'Retenir'}
        </button>
      </div>
    </form>
  );
}
